import type {
  Address,
  ContactPoint,
  Organization,
  Practitioner,
} from '@medplum/fhirtypes'
import { compact } from 'lodash-es'
import { z } from 'zod'
import { EXTERNAL_RESOURCE_JSON_SYSTEM } from '#src/codeSystems.ts'
import { elationSandboxIdentifier } from '#src/elation.ts'
import { build } from '#src/utils.ts'

const specialtySchema = z.object({
  id: z.number(),
  name: z.string(),
  abbreviation: z.string().nullable(),
  category: z
    .object({
      id: z.number(),
      name: z.string(),
      abbreviation: z.string().nullable(),
      created_date: z.string(),
    })
    .nullable(),
  created_date: z.string().nullable(),
})

export const contactSchema = z.object({
  id: z.number(),
  first_name: z.string().max(200),
  last_name: z.string().max(200),
  middle_name: z.string().max(200).nullable(),
  org_name: z.string().max(200).nullable(),
  fax: z.string().max(15).nullable(),
  direct_address: z.string().max(500).nullable(),
  specialty: specialtySchema.nullable(),
  other_specialties: z.array(specialtySchema),
  npi: z.string().nullable(),
  address: z.string().max(100),
  city: z.string().max(30),
  state: z.string().max(20),
  zip: z.string().max(10),
  suite: z.string().max(20).nullable(),
  contact_type: z.enum(['physician', 'medical_group']),
  credentials: z.string().max(20).nullable(),
  is_verified: z.boolean(),
  is_elation_confirmed: z.boolean(),
  phone: z.string().max(25).nullable(),
  cell_phone: z.string().max(25).nullable(),
  back_office_phone: z.string().max(25).nullable(),
  accepting_insurance: z.boolean().nullable(),
  accepting_new_patients: z.boolean().nullable(),
  created_date: z.string(),
  deleted_date: z.string().nullable(),
  email: z.string().nullable(),
  practice: z.number(),
  user: z.number(),
})

export type Contact = z.infer<typeof contactSchema>

export function toFhir(contact: Contact): Practitioner | Organization {
  const telecom: ContactPoint[] = []

  if (contact.phone) {
    telecom.push({
      system: 'phone',
      value: contact.phone,
      use: 'work',
    })
  }

  if (contact.cell_phone) {
    telecom.push({
      system: 'phone',
      value: contact.cell_phone,
      use: 'mobile',
    })
  }

  if (contact.back_office_phone) {
    telecom.push({
      system: 'phone',
      value: contact.back_office_phone,
      use: 'work',
    })
  }

  if (contact.fax) {
    telecom.push({
      system: 'fax',
      value: contact.fax,
      use: 'work',
    })
  }

  if (contact.email) {
    telecom.push({
      system: 'email',
      value: contact.email,
      use: 'work',
    })
  }

  const address: Address = {
    line: compact([contact.address.trim()]),
    city: contact.city,
    state: contact.state,
    postalCode: contact.zip,
  }

  if (contact.suite) {
    address.line?.push(contact.suite)
  }

  if (contact.contact_type === 'medical_group') {
    return build('Organization', {
      identifier: [
        elationSandboxIdentifier(contact.id.toString()),
        ...(contact.npi
          ? [
              {
                system: 'http://hl7.org/fhir/sid/us-npi',
                value: contact.npi,
              },
            ]
          : []),
      ],
      active: !contact.deleted_date,
      name: `${contact.first_name} ${contact.last_name} ${contact.org_name}`?.trim(),
      telecom,
      address: [address],
      extension: [
        {
          url: EXTERNAL_RESOURCE_JSON_SYSTEM,
          valueString: JSON.stringify(contact),
        },
      ],
    })
  }

  const qualification = []
  if (contact.specialty) {
    qualification.push({
      code: {
        text: contact.specialty.name,
      },
    })
  }

  if (contact.other_specialties) {
    contact.other_specialties.forEach((specialty) => {
      qualification.push({
        code: {
          text: specialty.name,
        },
      })
    })
  }

  return build('Practitioner', {
    identifier: [
      elationSandboxIdentifier(contact.id.toString()),
      ...(contact.npi
        ? [
            {
              system: 'http://hl7.org/fhir/sid/us-npi',
              value: contact.npi,
            },
          ]
        : []),
    ],
    active: !contact.deleted_date,
    name: [
      {
        use: 'official',
        family: contact.last_name,
        given: compact([
          contact.first_name,
          ...(contact.middle_name ? [contact.middle_name] : []),
        ]),
        prefix: contact.credentials ? [contact.credentials] : undefined,
      },
    ],
    telecom,
    address: [address],
    qualification,
    extension: [
      {
        url: EXTERNAL_RESOURCE_JSON_SYSTEM,
        valueString: JSON.stringify(contact),
      },
    ],
  })
}
